/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * The type of the edge.
 */
export var InspectableEdgeType;
(function (InspectableEdgeType) {
    /**
     * Just an ordinary edge. Most of the edges in graphs are ordinary.
     */
    InspectableEdgeType["Ordinary"] = "ordinary";
    /**
     * Constant edge has an effect of "memoizing" the value that passes
     * through it, make it always available. So when the incoming node is among
     * opportunities to visit again, the constant edge will report that it already
     * has the value.
     * Each new value that comes from the outgoing wire will overwrite the one
     * that is memoized.
     * Constant edges are primarily useful when building graphs with cycles.
     * For example, if you want to invoke some fetch multiple times, with the same
     * secret value, use the constant edge to connect the secret to the fetch.
     */
    InspectableEdgeType["Constant"] = "constant";
    /**
     * Control edge does not pass any data across. It is purely a control flow
     * wire, primarily useful when building graphs with cycles.
     */
    InspectableEdgeType["Control"] = "control";
    /**
     * Star edge is the opposite of control edge: it passes all data from outgoing
     * node to incoming node. Use it when you do not need to discern what
     * ports are being passed and their names match for the incoming/outgoing
     * nodes.
     */
    InspectableEdgeType["Star"] = "star";
})(InspectableEdgeType || (InspectableEdgeType = {}));
/**
 * Describes the current status of a node port.
 */
export var PortStatus;
(function (PortStatus) {
    /**
     * The port status impossible to determine. This only happens when the node
     * has a star wire ("*") and the port is not connected.
     */
    PortStatus["Indeterminate"] = "indeterminate";
    /**
     * The port is correctly connected to another node or specified using node's
     * configuration, according to this node's schema.
     */
    PortStatus["Connected"] = "connected";
    /**
     * The port is not connected to another node, and it is expected, but not
     * required by the node's schema.
     */
    PortStatus["Ready"] = "ready";
    /**
     * The port is not connected to another node, but it is required by the node's
     * schema. It is similar to "Ready", except that not having this port
     * connected is an error.
     */
    PortStatus["Missing"] = "missing";
    /**
     * The port is connected to this node, but it is not expected by the node's
     * schema. This is an error state.
     */
    PortStatus["Dangling"] = "dangling";
})(PortStatus || (PortStatus = {}));
//# sourceMappingURL=types.js.map